import { useEffect, useState } from 'react';

import { useCreateLineBindCode, useUnlinkLine } from '../api/mutations';
import type { LineAccount } from '../api/queries';
import { useLineStatusQuery } from '../api/queries';

const DEFAULT_TTL_SECONDS = 600;
const NO_ACCOUNTS: LineAccount[] = [];

interface BindSession {
    code: string;
    expiresAt: number;
    // Ids already linked when the code was minted; any other id means the bind landed.
    knownIds: Set<string>;
}

/** The linked LINE accounts, and the one-time-code handshake that links another. */
export const useLineBinding = () => {
    const [connecting, setConnecting] = useState(false);
    const [session, setSession] = useState<BindSession | null>(null);
    const [expiredSession, setExpiredSession] = useState<BindSession | null>(null);
    const expired = session !== null && expiredSession === session;

    const status = useLineStatusQuery({ poll: session !== null && !expired });
    const accounts = status.data ?? NO_ACCOUNTS;
    const createCode = useCreateLineBindCode();
    const unlink = useUnlinkLine();

    // Adjusted during render rather than in an effect, so the code panel never paints after the bind.
    if (session && accounts.some((account) => !session.knownIds.has(account.line_user_id))) {
        setSession(null);
        setConnecting(false);
    }

    useEffect(() => {
        if (!session) return undefined;
        const timer = setTimeout(() => setExpiredSession(session), session.expiresAt - Date.now());
        return () => clearTimeout(timer);
    }, [session]);

    // Each call mints a fresh single-use code, so request once per Connect / regenerate.
    const requestCode = () => {
        const knownIds = new Set(accounts.map((account) => account.line_user_id));
        setConnecting(true);
        unlink.reset();
        createCode.mutate(undefined, {
            onSuccess: ({ code, expires_in }) => {
                const ttl = expires_in && expires_in > 0 ? expires_in : DEFAULT_TTL_SECONDS;
                setSession({ code, expiresAt: Date.now() + ttl * 1000, knownIds });
            },
        });
    };

    // Resetting the mutation also drops a code that's still on its way.
    const cancel = () => {
        setConnecting(false);
        setSession(null);
        createCode.reset();
        unlink.reset();
    };

    return {
        accounts,
        loading: status.isPending,
        connecting,
        code: session?.code ?? '',
        expired,
        busy: createCode.isPending || unlink.isPending,
        failed: createCode.isError || unlink.isError,
        requestCode,
        cancel,
        unlink: (lineUserId: string) => unlink.mutate(lineUserId),
    };
};
